import { createRng, hashSeed } from './rng.js';
import { STAFF_BY_ID } from '../data/staff.js';
import { dueIn, spotFor } from './tasks.js';

/**
 * The ledger.
 *
 * Asking is free. That is the trap, and this module is the spring on it. An
 * owed favour sits in the ledger across shifts and comes back **on the
 * holder's schedule, not yours** — which in practice means the middle of a
 * setup, because that is when you are in the building and that is when it
 * hurts. A debt you can choose to repay when convenient is not a debt, it is a
 * quest log.
 *
 * Two rules do most of the work:
 *
 *   - A favour is not callable until at least `coolDays` later. It has to
 *     arrive far enough from the ask that the player has stopped thinking
 *     about it, or the whole thing reads as a delayed payment rather than as
 *     something coming back around.
 *   - The minute it fires is rolled once, from a seed, and stored. Rolling it
 *     live would mean a reload could move it, and a debt that changes its mind
 *     about when it is due is a debt nobody can plan against.
 *
 * Refusing is always allowed and always costs. There is no state in which the
 * game demands you drop what you are doing — it only ever makes you choose
 * which of two people you are letting down.
 */

/** How long you get to answer a called-in favour, in game minutes. */
export const FAVOR_WINDOW = 75;

/** What refusing costs the holder's department. */
export const REFUSAL_COST = 14;

/** What settling one is worth. Less than the refusal costs: debts are not profit. */
export const SETTLE_REWARD = 6;

export function createFavors({ tasks, standing, seed = 'lion' }) {
  let ledger = [];
  let counter = 0;
  /**
   * The last time `update` was told about.
   *
   * Refusing has to push the debt past today, and the only reliable "today" is
   * the one the ledger was last ticked with — a clock captured at construction
   * is the clock the game happens to have had at boot, and a caller reacting to
   * an expired errand has no clock of its own to offer.
   */
  let now = { day: 0, minutes: 0 };
  const listeners = new Set();

  function emit(event, payload) {
    for (const listener of listeners) listener(event, payload);
  }

  /**
   * When a favour will be called in.
   *
   * Rolled once, at the moment it is owed, from a stream keyed on the favour's
   * own id. Somewhere in the working part of the day, and never in the first
   * hour — turning up at 14:05 on day two to collect reads as the game waiting
   * for you rather than as a person remembering.
   */
  function scheduleCall(favor) {
    const rng = createRng(hashSeed(seed, 'favor', favor.id));
    favor.callDay = favor.day + Math.max(1, favor.coolDays);
    favor.callMinute = rng.int(9 * 60, 21 * 60);
    return favor;
  }

  return {
    onEvent(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    get list() {
      return ledger;
    },
    get owed() {
      return ledger.filter((favor) => favor.state === 'owed');
    },
    get count() {
      return ledger.filter((favor) => favor.state === 'owed').length;
    },
    /** What you owe, by department, for the shift summary. */
    get byDepartment() {
      const out = {};
      for (const favor of ledger) {
        if (favor.state !== 'owed') continue;
        out[favor.department] = (out[favor.department] ?? 0) + 1;
      }
      return out;
    },

    owe({ staffId, department, day, coolDays = 1, weight = 1, note = '', beoId = null }) {
      counter += 1;
      const favor = scheduleCall({
        id: `favor-${staffId}-${day}-${counter}`,
        staffId,
        department,
        day,
        coolDays,
        weight,
        note,
        beoId,
        state: 'owed',
      });
      ledger = [...ledger, favor];
      emit('owed', favor);
      return favor;
    },

    /**
     * Calls in whatever is due. Run every frame; fires at most one at a time.
     *
     * One at a time on purpose. Two debts landing in the same minute is not
     * twice the pressure, it is a pile-up the player cannot engage with, and
     * the second one would be refused on sight.
     */
    update(clockNow, { canInterrupt = true } = {}) {
      now = { day: clockNow.day, minutes: clockNow.minutes };
      if (!canInterrupt || tasks.full) return null;
      // One at a time. A second debt landing while the first is still standing
      // in the corridor is a pile-up the player cannot engage with, and it
      // would be refused on sight.
      if (ledger.some((favor) => favor.state === 'called')) return null;
      const due = ledger.find(
        (favor) =>
          favor.state === 'owed' &&
          (clockNow.day > favor.callDay ||
            (clockNow.day === favor.callDay && clockNow.minutes >= favor.callMinute))
      );
      if (!due) return null;

      const person = STAFF_BY_ID[due.staffId];
      const spot = spotFor(due.staffId);
      due.state = 'called';
      due.taskId = `favor-task-${due.id}`;
      tasks.add({
        id: due.taskId,
        source: 'favor',
        favorId: due.id,
        staffId: due.staffId,
        department: due.department,
        label: `${person.name} is calling it in`,
        target: spot,
        dueAbsolute: dueIn(clockNow, FAVOR_WINDOW),
        minutes: FAVOR_WINDOW,
        note: due.note,
      });
      emit('called', { favor: due, person });
      return due;
    },

    /** Paid. A small credit, because turning up is the minimum. */
    settle(favorId) {
      const favor = ledger.find((entry) => entry.id === favorId);
      if (!favor) return null;
      favor.state = 'settled';
      standing.applyDirect(
        { [favor.department]: SETTLE_REWARD },
        { reason: `Settled with ${STAFF_BY_ID[favor.staffId].name}` }
      );
      emit('settled', favor);
      return favor;
    },

    /**
     * Refused, or let expire. Always allowed, always costs.
     *
     * The debt does not go away — it goes back into the ledger and can be
     * called again, which is what makes refusing a decision to delay rather
     * than a way out.
     */
    refuse(favorId, { expired = false } = {}) {
      const favor = ledger.find((entry) => entry.id === favorId);
      if (!favor) return null;
      favor.state = 'owed';
      favor.refusals = (favor.refusals ?? 0) + 1;
      // Back into the queue, a day out, so it is not called again this shift.
      favor.callDay = Math.max(now.day, favor.callDay) + 1;
      standing.applyDirect(
        { [favor.department]: -REFUSAL_COST },
        {
          reason: expired
            ? `Left ${STAFF_BY_ID[favor.staffId].name} waiting`
            : `Turned ${STAFF_BY_ID[favor.staffId].name} down`,
        }
      );
      emit('refused', favor);
      return favor;
    },

    byTask: (taskId) => ledger.find((favor) => favor.taskId === taskId) ?? null,

    clear() {
      ledger = [];
      counter = 0;
    },

    toJSON: () => ({ ledger, counter }),
    load(state) {
      ledger = [...(state?.ledger ?? [])];
      counter = state?.counter ?? 0;
    },
  };
}
